import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { CaretDownIcon } from '@/components/icons';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useDownloadCharter } from '@/lib/hooks/useDownloadCharter';
import { useI18n } from '@/i18n/I18nProvider';

interface StudentActionsProps {
  studentId: string;
  studentNumber?: string | null;
  isActive?: boolean;
  variant?: 'default' | 'white';
  buttonClassName?: string;
}

function StudentActions({
  studentId,
  studentNumber,
  isActive = true,
  variant = 'default',
  buttonClassName,
}: StudentActionsProps) {
  const [isDesktopOpen, setIsDesktopOpen] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const { t } = useI18n();
  const { downloadCharter } = useDownloadCharter();

  const handleCloseMenu = () => {
    setIsDesktopOpen(false);
    setIsMobileOpen(false);
  };

  const actionItems = [
    {
      label: t.viewProfile,
      href: `/students/${studentId}`,
      isActive: true,
    },
    {
      label: t.updateInfo,
      href: `/students/${studentId}/update-info?mode=init`,
      isActive: Boolean(isActive),
    },
    {
      label: t.studentDocuments,
      href: `/students/${studentId}/update-documents`,
      isActive: Boolean(isActive),
    },
    {
      label: t.signCharter,
      href: `/students/${studentId}/sign-charter`,
      isActive: Boolean(isActive),
    },
    {
      label: t.downloadCharter,
      href: '#',
      onClick: (e: React.MouseEvent) => {
        e.preventDefault();
        downloadCharter({ studentNumber });
      },
      isActive: Boolean(isActive),
    },
  ];

  const activeActions = actionItems.filter((action) => action.isActive);

  const baseButtonClasses = clsx(
    'aegov-btn btn-xs gap-2 cursor-pointer w-full sm:w-auto',
    variant === 'white' ? 'bg-white text-primary hover:bg-white/95' : 'btn-primary',
    buttonClassName
  );

  return (
    <>
      {/* Desktop Dropdown (large screens) */}
      <div className="hidden sm:block">
        <Popover open={isDesktopOpen} onOpenChange={setIsDesktopOpen}>
          <PopoverTrigger asChild>
            <button type="button" className={baseButtonClasses}>
              <span>{t.actions}</span>
              <CaretDownIcon
                className={clsx('w-4 h-4 transition-transform duration-200', isDesktopOpen && 'rotate-180')}
              />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            className="w-52 p-1.5 bg-popover text-popover-foreground border shadow-md rounded-lg"
          >
            <div className="flex flex-col space-y-1">
              {activeActions.map((action) => (
                <Link
                  key={action.label}
                  to={action.href}
                  onClick={(e) => {
                    handleCloseMenu();
                    if (action.onClick) {
                      action.onClick(e);
                    }
                  }}
                  className="px-3 py-2 text-sm text-foreground hover:bg-accent hover:text-accent-foreground rounded-md transition-colors block no-underline font-medium"
                >
                  {action.label}
                </Link>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Mobile Activity Bar (bottom sheet on mobile screens) */}
      <div className="block sm:hidden">
        <Sheet open={isMobileOpen} onOpenChange={setIsMobileOpen}>
          <SheetTrigger asChild>
            <button type="button" className={baseButtonClasses}>
              <span>{t.actions}</span>
              <CaretDownIcon
                className={clsx('w-4 h-4 transition-transform duration-200', isMobileOpen && 'rotate-180')}
              />
            </button>
          </SheetTrigger>
          <SheetContent side="bottom" className="rounded-t-2xl px-5 pt-5 pb-8 space-y-4">
            <SheetHeader className="text-start pb-3 border-b border-border">
              <SheetTitle className="text-base font-semibold text-foreground">{t.studentActions}</SheetTitle>
            </SheetHeader>
            <div className="flex flex-col space-y-2 pt-1">
              {activeActions.map((action) => (
                <Link
                  key={action.label}
                  to={action.href}
                  onClick={(e) => {
                    handleCloseMenu();
                    if (action.onClick) {
                      action.onClick(e);
                    }
                  }}
                  className="px-4 py-3 text-sm font-medium text-foreground bg-muted/40 hover:bg-muted active:bg-muted/80 rounded-xl transition-colors block no-underline text-start"
                >
                  {action.label}
                </Link>
              ))}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </>
  );
}

export default StudentActions;
